import { Children, forwardRef, useCallback, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

import cn from 'classnames';

import { CarouselItem } from '../CarouselItem/CarouselItem';
import type { ObservableCarouselItemProps } from '../CarouselItem/CarouselItem';

import * as styles from './Carousel.module.scss';

export interface CarouselProps {
  currentIndex: number;
  onCurrentIndexChange: (
    value: number | ((prevState: number) => number)
  ) => void;
  children?: ReactNode;
  className?: string;
}

export const Carousel = forwardRef<HTMLDivElement, CarouselProps>(
  ({ children, currentIndex, onCurrentIndexChange, className }, ref) => {
    const listRef = useRef<HTMLUListElement>(null!);
    const childrenArray = Children.toArray(children);

    useEffect(() => {
      listRef.current.scrollTo({
        left: listRef.current.clientWidth * currentIndex,
      });
    }, [currentIndex]);

    const handleIntersectionChange: ObservableCarouselItemProps['onIntersectionChange'] =
      useCallback(
        (entry, index) => {
          if (entry.isIntersecting) {
            onCurrentIndexChange(index);
          }
        },
        [onCurrentIndexChange]
      );

    return (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <div className={cn(styles.carousel, className)} ref={ref}>
        <ul className={styles.list} ref={listRef}>
          {childrenArray.map((child, index) => (
            <CarouselItem
              key={index}
              onIntersectionChange={handleIntersectionChange}
              index={index}
              aria-hidden={index !== currentIndex}
              tabIndex={index === currentIndex ? 0 : -1}
              focusable
            >
              {child}
            </CarouselItem>
          ))}
        </ul>
      </div>
    );
  }
);
